import { create } from 'zustand';
import {
    ComposerAreaNode,
    ComposerAttributeNode,
    ComposerAttributeOptionNode,
    ComposerAttributeType,
    ComposerEcommerceSettings,
    ComposerExtensionFieldDefinition,
    ComposerMerchantProductOption,
    ComposerOptionActionNode,
    ComposerPrintTypeDefinition,
    ComposerTemplateNode,
    ComposerTemplateSideRestrictions
} from '../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { composerQueriesFontQuery$data } from './__generated__/composerQueriesFontQuery.graphql';
import { upsertAndValidateComposerOption, validateComposerAttributes, validateComposerOptions } from './helpers/composerAttributeValidation';
import { createSecureClonedName } from './helpers/composerHelpers';
import { ComposerTemplateNodeAdapter } from './helpers/composerNodesAdapter';
import { LazyValidateAttributes, LazyValidateOptions } from './helpers/composerValidation';

export type ComposerAttributeFace = 'options' | 'settings' | 'links';
export type ComposerOptionFace = 'actions' | 'settings' | 'links' | 'prices' | 'properties';

// attributes > options > actions
type ComposerStore = {
    model: MPlaza.Model | null;
    setModel: (model: MPlaza.Model) => void;

    //Scene specific
    sceneMaterialList: BABYLON.Material[];
    sceneObjectList: BABYLON.AbstractMesh[];
    sceneCameraList: BABYLON.Camera[];
    setSceneMaterialList: (materials: BABYLON.Material[]) => void;
    setSceneObjectList: (objects: BABYLON.AbstractMesh[]) => void;
    setSceneCameraList: (cameras: BABYLON.Camera[]) => void;

    modelAttributes: MPlaza.Attributes | null;

    selectedAttributes: ComposerAttributeNode[] | null;
    selectedOptions: ComposerAttributeOptionNode[] | null;
    selectedActions: ComposerOptionActionNode[] | null;
    attributeFace: ComposerAttributeFace;
    optionFace: ComposerOptionFace;
    setAttributeFace: (face: ComposerAttributeFace) => void;
    setOptionFace: (face: ComposerOptionFace) => void;

    attributes: ComposerAttributeNode[];
    attributesSteps: ComposerAttributeNode[] | null;
    attributesGroups: ComposerAttributeNode[] | null;

    templates: ComposerTemplateNode[];
    selectedTemplates: ComposerTemplateNode[] | null;
    areas: ComposerAreaNode[];
    selectedAreas: ComposerAreaNode[] | null;
    designs: Map<string, MPlaza.Design>; //key: templateID, areaID
    getDesign: (templateID: number, areaID: number) => MPlaza.Design | null;
    setDesigns: (designs: Map<string, MPlaza.Design>) => void;
    updateDesign: (templateID: number, areaID: number, design: MPlaza.Design) => void;

    //quantity settings page
    productQuantity: MPlaza.QuantityRule | null;

    ecommerceSettings: ComposerEcommerceSettings | null;

    merchantProductOptions: ComposerMerchantProductOption[];
    merchantProductOptionsError: string | null;
    printTypes: ComposerPrintTypeDefinition[];
    extensionFieldDefinitions: ComposerExtensionFieldDefinition[];
    setRequiredEditorData: (data: {
        merchantProductOptions: ComposerMerchantProductOption[];
        merchantProductOptionsError: string | null;
        printTypes: ComposerPrintTypeDefinition[];
        extensionFieldDefinitions: ComposerExtensionFieldDefinition[];
    }) => void;

    fonts: composerQueriesFontQuery$data | null;
    setFonts: (fonts: composerQueriesFontQuery$data) => void;

    intializeAttributesInfo: (modelAttributes: MPlaza.Attributes, attributes: ComposerAttributeNode[]) => void;
    updateAttribute: (attribute: ComposerAttributeNode) => void;
    cloneAttributes: (attributes: ComposerAttributeNode[]) => ComposerAttributeNode[];
    removeAttributes: (attributeIds: string[]) => void;
    updateOption: (option: ComposerAttributeOptionNode) => void;
    cloneOption: (option: ComposerAttributeOptionNode) => ComposerAttributeOptionNode;
    removeOption: (attributeID: string, optionID: string) => void;
    updateAction: (action: ComposerOptionActionNode) => void;
    cloneAction: (action: ComposerOptionActionNode) => ComposerOptionActionNode;
    removeAction: (attributeID: string, optionID: string, actionID: string) => void;

    updateTemplate: (template: ComposerTemplateNode) => void;
    updateTemplateRestriction: (template: ComposerTemplateNode, sideID: number, key: keyof ComposerTemplateSideRestrictions, value: boolean) => void;
    cloneTemplate: (template: ComposerTemplateNode) => ComposerTemplateNode;
    removeTemplate: (templateID: string) => void;
    updateArea: (area: ComposerAreaNode) => void;
    cloneArea: (area: ComposerAreaNode) => ComposerAreaNode;
    removeArea: (areaID: string) => void;
    initializeTemplateInfo: (templates: ComposerTemplateNode[], areas: ComposerAreaNode[]) => void;

    setEcommerceSettings: (ecommerceSettings: ComposerEcommerceSettings) => void;
    selectAttributes: (attribute: ComposerAttributeNode[] | null) => void;
    selectOptions: (options: ComposerAttributeOptionNode[] | null) => void;
    selectActions: (actions: ComposerOptionActionNode[] | null) => void;
    selectTemplates: (templates: ComposerTemplateNode[] | null) => void;
    selectAreas: (areas: ComposerAreaNode[] | null) => void;

    setProductQuantity: (quantity: MPlaza.QuantityRule | null) => void;
    validateAttributes: () => Promise<ComposerAttributeNode[]>;
    validateOptions: (attribute: ComposerAttributeNode) => Promise<ComposerAttributeOptionNode[]>;
};

// Create store with the modern create pattern
const useComposerStore = create<ComposerStore>((set, store) => {
    return {
        model: null,
        setModel: (model) => set({ model }),

        sceneMaterialList: [],
        sceneObjectList: [],
        sceneCameraList: [],
        setSceneMaterialList: (materials) => set({ sceneMaterialList: materials }),
        setSceneObjectList: (objects) => set({ sceneObjectList: objects }),
        setSceneCameraList: (cameras) => set({ sceneCameraList: cameras }),

        modelAttributes: null,
        attributes: [],
        attributesSteps: null,
        selectedAttributes: null,
        selectedOptions: null,
        selectedActions: null,
        attributeFace: 'options',
        optionFace: 'actions',
        setAttributeFace: (attributeFace) => set({ attributeFace }),
        setOptionFace: (optionFace) => set({ optionFace }),
        attributesGroups: null,

        templates: [],
        selectedTemplates: null,
        areas: [],
        designs: new Map(),
        setDesigns: (designs: Map<string, MPlaza.Design>) => set({ designs }),
        selectedAreas: null,

        ecommerceSettings: null,
        merchantProductOptions: [],
        merchantProductOptionsError: null,
        printTypes: [],
        extensionFieldDefinitions: [],
        setRequiredEditorData: (data) => set(data),
        fonts: null,
        setFonts: (fonts) => set({ fonts }),
        productQuantity: null,
        intializeAttributesInfo: (modelAttributes, attributes) =>
            set({
                modelAttributes,
                attributes,
                attributesSteps: attributes.filter((attribute) => attribute.type === ComposerAttributeType.AttributeStep),
                attributesGroups: attributes.filter((attribute) => attribute.type === ComposerAttributeType.AttributeGroup),
                selectedAttributes: null,
                selectedOptions: null,
                selectedActions: null,
                attributeFace: 'options',
                optionFace: 'actions'
            }),
        setEcommerceSettings: (ecommerceSettings) => set({ ecommerceSettings }),
        selectAttributes: (attributes) => {
            // Reset selectedOptions only if the attribute selection actually changed
            set((state) => {
                const previousAttributeIds =
                    state.selectedAttributes
                        ?.map((attr) => attr.id)
                        .sort()
                        .join(',') || '';
                const newAttributeIds =
                    attributes
                        ?.map((attr) => attr.id)
                        .sort()
                        .join(',') || '';
                const attributeChanged = previousAttributeIds !== newAttributeIds;

                return {
                    selectedAttributes: attributes,
                    selectedOptions: attributeChanged ? [] : state.selectedOptions,
                    selectedActions: attributeChanged ? null : state.selectedActions,
                    attributeFace: attributeChanged ? 'options' : state.attributeFace
                };
            });
        },
        selectOptions: (options) =>
            set((state) => {
                const previousOptionIds = state.selectedOptions?.map((option) => option.id).join(',') || '';
                const nextOptionIds = options?.map((option) => option.id).join(',') || '';
                const optionChanged = previousOptionIds !== nextOptionIds;
                return {
                    selectedOptions: options,
                    selectedActions: optionChanged ? null : state.selectedActions,
                    optionFace: optionChanged ? 'actions' : state.optionFace
                };
            }),
        selectActions: (actions) => set({ selectedActions: actions }),
        selectTemplates: (templates) => set({ selectedTemplates: templates }),
        selectAreas: (areas) => set({ selectedAreas: areas }),
        //update if present, otherwise add new attribute
        updateAttribute: (attribute) => {
            set((state) => {
                const attr = state.attributes.find((a) => a.id === attribute.id);

                // Update selectedAttributes if this attribute is currently selected
                const updatedSelectedAttributes =
                    state.selectedAttributes?.map((selectedAttr) => (selectedAttr.id === attribute.id ? attribute : selectedAttr)) ?? null;

                if (attr) {
                    const updatedSelectedOptions =
                        state.selectedOptions?.map((selectedOption) => attribute.options.find((option) => option.id === selectedOption.id) ?? selectedOption) ??
                        null;
                    return {
                        attributes: state.attributes.map((a) => (a.id === attribute.id ? attribute : a)),
                        selectedAttributes: updatedSelectedAttributes,
                        selectedOptions: updatedSelectedOptions
                    };
                }
                return {
                    attributes: [...state.attributes, attribute],
                    selectedAttributes: updatedSelectedAttributes,
                    selectedOptions: state.selectedOptions
                };
            });
        },
        //append _clone to the id of cloned attributes
        cloneAttributes: (attributes) => {
            let clonedAttributes: ComposerAttributeNode[] = [];
            set((state) => {
                const attributeIdMap = new Map(attributes.map((attribute) => [attribute.id, Zakeke.generateUUID()]));
                const optionIdMap = new Map(attributes.flatMap((attribute) => attribute.options.map((option) => [option.id, Zakeke.generateUUID()] as const)));
                const nextOrderByParent = new Map<string, number>();
                const names = state.attributes.map((attribute) => attribute.name);
                const codes = state.attributes.map((attribute) => attribute.code);

                const nextDisplayOrder = (parentID?: string) => {
                    const key = parentID || '__root__';
                    const current = nextOrderByParent.get(key);
                    if (current != null) {
                        nextOrderByParent.set(key, current + 1);
                        return current;
                    }
                    const next =
                        Math.max(-1, ...state.attributes.filter((attribute) => attribute.parentID === parentID).map((attribute) => attribute.displayOrder)) + 1;
                    nextOrderByParent.set(key, next + 1);
                    return next;
                };

                clonedAttributes = attributes.map((attr) => {
                    const clonedParentID = attr.parentID ? attributeIdMap.get(attr.parentID) || attr.parentID : undefined;
                    const clonedAttr: ComposerAttributeNode = {
                        ...attr,
                        id: attributeIdMap.get(attr.id)!,
                        idNumeric: -1,
                        isNew: true,
                        parentID: clonedParentID,
                        parentIDNumeric: attr.parentID && attributeIdMap.has(attr.parentID) ? -1 : attr.parentIDNumeric,
                        displayOrder: attr.parentID && attributeIdMap.has(attr.parentID) ? attr.displayOrder : nextDisplayOrder(clonedParentID)
                    };
                    clonedAttr.name = createSecureClonedName(clonedAttr.name, names);
                    names.push(clonedAttr.name);
                    clonedAttr.code = createSecureClonedName(clonedAttr.code, codes);
                    codes.push(clonedAttr.code);
                    clonedAttr.options = attr.options.map((option) => {
                        const clonedOption = {
                            ...option,
                            id: optionIdMap.get(option.id)!,
                            idNumeric: -1,
                            attributeID: clonedAttr.id,
                            isNew: true
                        };
                        clonedOption.name = createSecureClonedName(
                            clonedOption.name,
                            attr.options.map((candidate) => candidate.name)
                        );
                        clonedOption.code = createSecureClonedName(
                            clonedOption.code,
                            attr.options.map((candidate) => candidate.code)
                        );
                        clonedOption.actions = clonedOption.actions.map((action) => ({
                            ...action,
                            id: Zakeke.generateUUID(),
                            attributeID: clonedAttr.id,
                            optionID: clonedOption.id
                        }));
                        clonedOption.links = clonedOption.links.map((link) => ({
                            ...link,
                            attributeID: attributeIdMap.get(link.attributeID) || link.attributeID,
                            optionID: optionIdMap.get(link.optionID) || link.optionID
                        }));
                        return clonedOption;
                    });
                    clonedAttr.pricings = attr.pricings.map((pricing) => ({
                        ...pricing,
                        attributePricingID: 0,
                        selectedOptionID: optionIdMap.has(pricing.selectedOptionGuid) ? -1 : pricing.selectedOptionID,
                        selectedOptionGuid: optionIdMap.get(pricing.selectedOptionGuid) || pricing.selectedOptionGuid
                    }));
                    clonedAttr.links = clonedAttr.links.map((link) => ({
                        ...link,
                        attributeID: attributeIdMap.get(link.attributeID) || link.attributeID,
                        optionID: optionIdMap.get(link.optionID) || link.optionID
                    }));
                    return clonedAttr;
                });
                return { attributes: [...state.attributes, ...clonedAttributes] };
            });
            return clonedAttributes;
        },
        removeAttributes: (attributeIds) =>
            set((state) => {
                // Remove deleted attributes from selectedAttributes to prevent stale references
                const updatedSelectedAttributes = state.selectedAttributes?.filter((attr) => !attributeIds.includes(attr.id)) ?? null;

                return {
                    attributes: state.attributes.filter((attr) => !attributeIds.includes(attr.id)),
                    selectedAttributes: updatedSelectedAttributes?.length ? updatedSelectedAttributes : null,
                    selectedOptions: null,
                    selectedActions: null
                };
            }),
        updateOption: (option) =>
            set((state) => {
                const attributeID = option.attributeID;
                const attributeIndex = state.attributes.findIndex((attr) => attr.id === attributeID);

                if (attributeIndex === -1) {
                    return state;
                }

                const attribute = state.attributes[attributeIndex];
                const updatedAttribute = upsertAndValidateComposerOption(attribute, option);
                const updatedOption = updatedAttribute.options.find((candidate) => candidate.id === option.id) ?? option;

                // Update selectedAttributes if this attribute is currently selected
                // This prevents the attribute from being deselected when options change
                const updatedSelectedAttributes =
                    state.selectedAttributes?.map((selectedAttr) => (selectedAttr.id === updatedAttribute.id ? updatedAttribute : selectedAttr)) ?? null;

                // Update selectedOptions if the updated option is currently selected
                const updatedSelectedOptions =
                    state.selectedOptions?.map((selectedOpt) => (selectedOpt.id === updatedOption.id ? updatedOption : selectedOpt)) ?? null;

                return {
                    ...state,
                    attributes: [...state.attributes.slice(0, attributeIndex), updatedAttribute, ...state.attributes.slice(attributeIndex + 1)],
                    selectedAttributes: updatedSelectedAttributes,
                    selectedOptions: updatedSelectedOptions
                };
            }),
        cloneOption: (option) => {
            let clonedOption: ComposerAttributeOptionNode | null = null;
            set((state) => {
                const attributeID = option.attributeID;
                const attributeIndex = state.attributes.findIndex((attr) => attr.id === attributeID);
                const attribute = state.attributes[attributeIndex];
                if (attributeIndex > -1) {
                    clonedOption = {
                        ...option,
                        id: Zakeke.generateUUID(),
                        idNumeric: -1,
                        displayOrder: Math.max(-1, ...attribute.options.map((candidate) => candidate.displayOrder)) + 1,
                        isDefault: false,
                        isNew: true
                    };
                    clonedOption.name = createSecureClonedName(
                        clonedOption.name,
                        attribute.options.map((opt) => opt.name)
                    );
                    clonedOption.code = createSecureClonedName(
                        clonedOption.code,
                        attribute.options.map((opt) => opt.code)
                    );
                    const clonedOptionID = clonedOption.id;
                    clonedOption.actions = clonedOption.actions.map((action) => ({
                        ...action,
                        id: Zakeke.generateUUID(),
                        attributeID,
                        optionID: clonedOptionID
                    }));
                    const updatedOptions = [...state.attributes[attributeIndex].options, clonedOption];
                    const clonedPricings = attribute.pricings
                        .filter((pricing) => pricing.selectedOptionGuid === option.id)
                        .map((pricing) => ({
                            ...pricing,
                            attributePricingID: 0,
                            selectedOptionID: -1,
                            selectedOptionGuid: clonedOptionID
                        }));
                    const updatedAttribute = {
                        ...state.attributes[attributeIndex],
                        options: updatedOptions,
                        pricings: [...attribute.pricings, ...clonedPricings]
                    };

                    // Update selectedAttributes if this attribute is currently selected
                    const updatedSelectedAttributes =
                        state.selectedAttributes?.map((selectedAttr) => (selectedAttr.id === updatedAttribute.id ? updatedAttribute : selectedAttr)) ?? null;

                    return {
                        attributes: state.attributes.map((attr, index) => (index === attributeIndex ? updatedAttribute : attr)),
                        selectedAttributes: updatedSelectedAttributes
                    };
                }
                return state;
            });
            return clonedOption!;
        },
        removeOption: (attributeID, optionID) =>
            set((state) => {
                const attributeIndex = state.attributes.findIndex((attr) => attr.id === attributeID);
                if (attributeIndex === -1) {
                    return state;
                }

                const attribute = state.attributes[attributeIndex];
                const removedOption = attribute.options.find((option) => option.id === optionID);
                const remainingOptions = attribute.options.filter((option) => option.id !== optionID);
                const options =
                    removedOption?.isDefault && remainingOptions.length > 0
                        ? remainingOptions.map((option, index) => ({ ...option, isDefault: index === 0 }))
                        : remainingOptions;

                const updatedAttribute = {
                    ...attribute,
                    options,
                    pricings: attribute.pricings.filter((pricing) => pricing.selectedOptionGuid !== optionID)
                };

                return {
                    ...state,
                    attributes: [...state.attributes.slice(0, attributeIndex), updatedAttribute, ...state.attributes.slice(attributeIndex + 1)],
                    selectedAttributes:
                        state.selectedAttributes?.map((selected) => (selected.id === updatedAttribute.id ? updatedAttribute : selected)) ?? null,
                    selectedOptions:
                        state.selectedOptions
                            ?.filter((option) => option.id !== optionID)
                            .map((option) => options.find((updatedOption) => updatedOption.id === option.id) ?? option) || null,
                    selectedActions: state.selectedActions?.filter((action) => action.optionID !== optionID) || null
                };
            }),
        //action childs of options
        updateAction: (action) =>
            set((state) => {
                const optionID = action.optionID;
                const attributeID = action.attributeID;

                const attributeIndex = state.attributes.findIndex((attr) => attr.id === attributeID);
                if (attributeIndex === -1) {
                    return state;
                }

                const attribute = state.attributes[attributeIndex];
                const optionIndex = attribute.options.findIndex((opt) => opt.id === optionID);
                if (optionIndex === -1) {
                    return state;
                }

                const option = attribute.options[optionIndex];
                const actionInOption = option.actions.find((a) => a.id === action.id);

                const updatedOption = {
                    ...option,
                    actions: actionInOption ? option.actions.map((a) => (a.id === action.id ? action : a)) : [...option.actions, action]
                };

                const updatedAttribute = {
                    ...attribute,
                    options: [...attribute.options.slice(0, optionIndex), updatedOption, ...attribute.options.slice(optionIndex + 1)]
                };

                // Update selectedAttributes if this attribute is currently selected
                const updatedSelectedAttributes =
                    state.selectedAttributes?.map((selectedAttr) => (selectedAttr.id === updatedAttribute.id ? updatedAttribute : selectedAttr)) ?? null;

                // Update selectedOptions if this option is currently selected
                const updatedSelectedOptions =
                    state.selectedOptions?.map((selectedOpt) => (selectedOpt.id === updatedOption.id ? updatedOption : selectedOpt)) ?? null;

                return {
                    ...state,
                    attributes: [...state.attributes.slice(0, attributeIndex), updatedAttribute, ...state.attributes.slice(attributeIndex + 1)],
                    selectedAttributes: updatedSelectedAttributes,
                    selectedOptions: updatedSelectedOptions,
                    selectedActions: state.selectedActions?.map((selectedAction) => (selectedAction.id === action.id ? action : selectedAction)) ?? null
                };
            }),
        cloneAction: (action) => {
            let clonedAction = null;
            set((state) => {
                const attributeID = action.attributeID;
                const optionID = action.optionID;
                const attributeIndex = state.attributes.findIndex((attr) => attr.id === attributeID);
                const optionIndex = attributeIndex > -1 ? state.attributes[attributeIndex].options.findIndex((opt) => opt.id === optionID) : -1;

                if (attributeIndex > -1 && optionIndex > -1) {
                    clonedAction = {
                        ...action,
                        id: Zakeke.generateUUID(),
                        displayOrder:
                            Math.max(-1, ...state.attributes[attributeIndex].options[optionIndex].actions.map((candidate) => candidate.displayOrder)) + 1,
                        isNew: true
                    };
                    const updatedActions = [...state.attributes[attributeIndex].options[optionIndex].actions, clonedAction];
                    const updatedOption = { ...state.attributes[attributeIndex].options[optionIndex], actions: updatedActions };
                    const updatedAttribute = {
                        ...state.attributes[attributeIndex],
                        options: state.attributes[attributeIndex].options.map((opt, index) => (index === optionIndex ? updatedOption : opt))
                    };

                    // Update selectedAttributes if this attribute is currently selected
                    const updatedSelectedAttributes =
                        state.selectedAttributes?.map((selectedAttr) => (selectedAttr.id === updatedAttribute.id ? updatedAttribute : selectedAttr)) ?? null;

                    // Update selectedOptions if this option is currently selected
                    const updatedSelectedOptions =
                        state.selectedOptions?.map((selectedOpt) => (selectedOpt.id === updatedOption.id ? updatedOption : selectedOpt)) ?? null;

                    return {
                        attributes: state.attributes.map((attr, index) => (index === attributeIndex ? updatedAttribute : attr)),
                        selectedAttributes: updatedSelectedAttributes,
                        selectedOptions: updatedSelectedOptions
                    };
                }
                return state;
            });
            return clonedAction!;
        },
        removeAction: (attributeID, optionID, actionID) =>
            set((state) => {
                const attributeIndex = state.attributes.findIndex((attr) => attr.id === attributeID);
                if (attributeIndex === -1) {
                    return state;
                }
                const attribute = state.attributes[attributeIndex];
                const optionIndex = attribute.options.findIndex((opt) => opt.id === optionID);
                if (optionIndex === -1) {
                    return state;
                }

                const option = attribute.options[optionIndex];

                const updatedOption = {
                    ...option,
                    actions: option.actions.filter((a) => a.id !== actionID)
                };

                const updatedAttribute = {
                    ...attribute,
                    options: [...attribute.options.slice(0, optionIndex), updatedOption, ...attribute.options.slice(optionIndex + 1)]
                };

                // Update selectedAttributes if this attribute is currently selected
                const updatedSelectedAttributes =
                    state.selectedAttributes?.map((selectedAttr) => (selectedAttr.id === updatedAttribute.id ? updatedAttribute : selectedAttr)) ?? null;

                // Update selectedOptions if this option is currently selected
                const updatedSelectedOptions =
                    state.selectedOptions?.map((selectedOpt) => (selectedOpt.id === updatedOption.id ? updatedOption : selectedOpt)) ?? null;

                return {
                    ...state,
                    attributes: [...state.attributes.slice(0, attributeIndex), updatedAttribute, ...state.attributes.slice(attributeIndex + 1)],
                    selectedAttributes: updatedSelectedAttributes,
                    selectedOptions: updatedSelectedOptions,
                    selectedActions: state.selectedActions?.filter((action) => action.id !== actionID) || null
                };
            }),

        updateTemplate: (template) =>
            set((state) => {
                const index = state.templates.findIndex((t) => t.id === template.id);

                // Update selectedTemplates if this template is currently selected
                const updatedSelectedTemplates =
                    state.selectedTemplates?.map((selectedTemplate) => (selectedTemplate.id === template.id ? template : selectedTemplate)) ?? null;

                if (index > -1) {
                    return {
                        templates: state.templates.map((t) => (t.id === template.id ? template : t)),
                        selectedTemplates: updatedSelectedTemplates
                    };
                }
                return {
                    templates: [...state.templates, template],
                    selectedTemplates: updatedSelectedTemplates
                };
            }),
        updateTemplateRestriction: (template, sideID, key, value) => {
            set((state) => {
                const index = state.templates.findIndex((t) => t.id === template.id);
                if (index === -1) {
                    return state;
                }
                const updatedRestrictions = new Map(template.restrictions);
                const sideRestrictions = updatedRestrictions.get(sideID) || {};
                sideRestrictions[key] = value;
                updatedRestrictions.set(sideID, sideRestrictions);
                const updatedTemplate = { ...template, restrictions: updatedRestrictions };

                // Update selectedTemplates if this template is currently selected
                const updatedSelectedTemplates =
                    state.selectedTemplates?.map((selectedTemplate) => (selectedTemplate.id === template.id ? updatedTemplate : selectedTemplate)) ?? null;
                return {
                    templates: state.templates.map((t) => (t.id === template.id ? updatedTemplate : t)),
                    selectedTemplates: updatedSelectedTemplates
                };
            });
        },
        getDesign: (templateID, areaID) => {
            const key = [templateID, areaID].join(',');
            return store().designs.get(key) || null;
        },
        updateDesign: (templateID, areaID, design) => {
            set((state) => {
                const key = [templateID, areaID].join(',');
                const updatedDesigns = new Map(state.designs);
                updatedDesigns.set(key, design);
                return {
                    ...state,
                    designs: updatedDesigns
                };
            });
        },
        cloneTemplate: (template) => {
            const clonedTemplate = { ...template, id: Zakeke.generateUUID() };
            set((state) => ({
                ...state,
                templates: [...state.templates, clonedTemplate]
                // No need to update selectedTemplates as cloned item is new
            }));
            return clonedTemplate;
        },
        removeTemplate: (templateID) =>
            set((state) => {
                // Remove deleted template from selectedTemplates to prevent stale references
                const updatedSelectedTemplates = state.selectedTemplates?.filter((t) => t.id !== templateID) ?? null;

                return {
                    ...state,
                    templates: state.templates.filter((t) => t.id !== templateID),
                    selectedTemplates: updatedSelectedTemplates?.length ? updatedSelectedTemplates : null
                };
            }),
        updateArea: (area) => {
            set((state) => {
                const index = state.areas.findIndex((a) => a.id === area.id);

                // Update selectedAreas if this area is currently selected
                const updatedSelectedAreas = state.selectedAreas?.map((selectedArea) => (selectedArea.id === area.id ? area : selectedArea)) ?? null;

                if (index > -1) {
                    return {
                        areas: state.areas.map((a) => (a.id === area.id ? area : a)),
                        selectedAreas: updatedSelectedAreas
                    };
                }
                const templates = state.templates;
                const designs = state.designs;
                if (!state.templates.length) {
                    const template = ComposerTemplateNodeAdapter.createNewTemplate('Default Template', state.model!, area.originalSide!);
                    templates.push(template);
                    designs.set([templates[0].templateID, area.sideID].join(','), template.design);
                }

                return {
                    areas: [...state.areas, area],
                    selectedAreas: updatedSelectedAreas,
                    templates,
                    designs
                };
            });
        },
        cloneArea: (area) => {
            const clonedArea = { ...area, id: Zakeke.generateUUID() };
            set((state) => ({
                ...state,
                areas: [...state.areas, clonedArea]
                // No need to update selectedAreas as cloned item is new
            }));
            return clonedArea;
        },
        removeArea: (areaID) =>
            set((state) => {
                // Remove deleted area from selectedAreas to prevent stale references
                const updatedSelectedAreas = state.selectedAreas?.filter((a) => a.id !== areaID) ?? null;

                return {
                    ...state,
                    areas: state.areas.filter((a) => a.id !== areaID),
                    selectedAreas: updatedSelectedAreas?.length ? updatedSelectedAreas : null
                };
            }),
        initializeTemplateInfo: (templates: ComposerTemplateNode[], areas: ComposerAreaNode[]) => {
            set((state) => {
                const designs = new Map();
                for (const template of templates) {
                    for (const area of areas) {
                        const key = [template.templateID, area.sideID].join(',');
                        designs.set(key, template.design);
                    }
                }
                return {
                    templates,
                    areas,
                    designs,
                    selectedTemplates: null,
                    selectedAreas: null
                };
            });
        },
        validateAttributes: () => {
            return LazyValidateAttributes(store().attributes).then(() => {
                const validatedAttributes = validateComposerAttributes(store().attributes);
                set((state) => {
                    // Update selectedAttributes to reflect validated attributes
                    const updatedSelectedAttributes =
                        state.selectedAttributes?.map((selectedAttr) => validatedAttributes.find((attr) => attr.id === selectedAttr.id) ?? selectedAttr) ??
                        null;

                    return {
                        attributes: validatedAttributes,
                        selectedAttributes: updatedSelectedAttributes
                    };
                });
                return validatedAttributes;
            });
        },
        validateOptions: (attribute: ComposerAttributeNode) => {
            const currentAttribute = store().attributes.find((attr) => attr.id === attribute.id);
            if (!currentAttribute) return Promise.resolve(attribute.options);

            return LazyValidateOptions(currentAttribute, currentAttribute.options).then(() => {
                const latestAttribute = store().attributes.find((candidate) => candidate.id === attribute.id);
                if (!latestAttribute) return [];
                const validatedOptions = validateComposerOptions(latestAttribute, latestAttribute.options);
                set((state) => {
                    const attributeIndex = state.attributes.findIndex((attr) => attr.id === attribute.id);
                    if (attributeIndex > -1) {
                        const updatedAttribute = { ...state.attributes[attributeIndex], options: validatedOptions };

                        // Update selectedAttributes if this attribute is currently selected
                        const updatedSelectedAttributes =
                            state.selectedAttributes?.map((selectedAttr) => (selectedAttr.id === updatedAttribute.id ? updatedAttribute : selectedAttr)) ??
                            null;

                        // Update selectedOptions to reflect the validated options
                        const updatedSelectedOptions =
                            state.selectedOptions?.map((selectedOpt) => validatedOptions.find((opt) => opt.id === selectedOpt.id) ?? selectedOpt) ?? null;

                        return {
                            attributes: state.attributes.map((attr, index) => (index === attributeIndex ? updatedAttribute : attr)),
                            selectedAttributes: updatedSelectedAttributes,
                            selectedOptions: updatedSelectedOptions
                        };
                    }
                    return state;
                });
                return validatedOptions;
            });
        },
        setProductQuantity: (quantity: MPlaza.QuantityRule | null) => {
            set((state) => ({
                ...state,
                productQuantity: quantity
            }));
        }
    };
});

// Selector helper (optional)
export const useComposerModelAttributes = () => useComposerStore((state) => state.modelAttributes);
export const useComposerAttributes = () => useComposerStore((state) => state.attributes);
export const useComposerAttributesSteps = () => useComposerStore((state) => state.attributesSteps);
export const useComposerAttributesGroups = () => useComposerStore((state) => state.attributesGroups);

export const useComposerEcommerceSettings = () => useComposerStore((state) => state.ecommerceSettings);

export const useSetComposerAttributes = () => useComposerStore((state) => state.intializeAttributesInfo);
export const useUpdateComposerAttributes = () => useComposerStore((state) => state.updateAttribute);
export const useSetComposerEcommerceSettings = () => useComposerStore((state) => state.setEcommerceSettings);

export const useSelectedComposerAttribute = () => useComposerStore((state) => state.selectedAttributes);
export const useSelectedComposerOptions = () => useComposerStore((state) => state.selectedOptions);
export const useSelectedComposerActions = () => useComposerStore((state) => state.selectedActions);

export const useComposerAttributesSelect = () => useComposerStore((state) => state.selectAttributes);
export const useComposerOptionsSelect = () => useComposerStore((state) => state.selectOptions);
export const useComposerActionsSelect = () => useComposerStore((state) => state.selectActions);

export default useComposerStore;
